import React from 'react';
import { Perk } from 'components/loyalty/models/PerkModel';
import { IAwardEnrichment } from 'components/loyalty/models/AwardEnrichment';
import { titleize } from 'lib/helpers';
import { GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { MuiGrid } from 'lib/MuiGrid/MuiGrid';
import { Paper, Tooltip, Typography } from '@mui/material';
import { renderLocationsCell } from './PerkLocationsCell';
import { SEARCHABLE_FIELDS } from './PerksSearchInfoTooltip';
import { GridStorageName } from 'lib/MuiGrid/StateController';
import { isArray, isDefined } from 'lib/typeguards';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';

export const PERKS_ITEMS_PER_PAGE = 100;
const AWARDS_GRANTED_TOOLTIP =
    'This count will include perks granted after the 15th April 2025, but for some perks may include incomplete data from 16th December onward.';
const TOTAL_REDEMPTIONS_TOOLTIP =
    'This count will include perk redemptions after the 15th April 2025, but for some perks may include incomplete data from 16th December onward.';

type PerksGridProps = {
    data: (Perk | IAwardEnrichment)[];
    isLoading: boolean;
    columnsToExclude?: string[];
    selectedRow: GridRowSelectionModel;
    searchValue: string;
    handleSelectRow: (newSelectedRow: GridRowSelectionModel) => void;
    storageName?: GridStorageName;
};

const formatValue = (alternative: string) => (value: string | undefined) => value || alternative;

const renderCountCell = (message: string) => (params: GridRenderCellParams) => {
    const updatedAt = params.row?.updatedAt;
    const showTooltipMessage = updatedAt && new Date(updatedAt) < new Date('2025-04-15');
    return (
        <Tooltip title={showTooltipMessage ? message : ''}>
            <Typography variant="body2">{params.value || '-'}</Typography>
        </Tooltip>
    );
};

export const gridColumns: GridColDef[] = [
    { field: '_id', headerName: 'ID', width: 150 },
    {
        field: 'loyaltyRewardId',
        headerName: 'Loyalty Reward ID',
        width: 200,
        valueFormatter: formatValue('-')
    },
    {
        field: 'internalTitle',
        headerName: 'Internal Title',
        width: 200,
        valueFormatter: formatValue('-')
    },
    { field: 'title', headerName: 'Title', width: 200, valueFormatter: formatValue('-') },
    {
        field: 'template',
        headerName: 'Type',
        width: 150
    },
    {
        field: 'enabled',
        headerName: 'Live',
        type: 'boolean'
    },
    {
        field: 'isGlobal',
        headerName: 'Global',
        type: 'boolean'
    },
    {
        field: 'totalAwarded',
        headerName: 'Awards Granted',
        renderCell: renderCountCell(AWARDS_GRANTED_TOOLTIP),
        width: 150
    },
    {
        field: 'maxTotalRedemptions',
        headerName: 'Maximum Redemptions',
        valueFormatter: formatValue('Unlimited'),
        width: 180
    },
    {
        field: 'totalRedeemed',
        headerName: 'Total Redemptions',
        renderCell: renderCountCell(TOTAL_REDEMPTIONS_TOOLTIP),
        width: 150
    },
    {
        field: 'locations',
        headerName: 'Locations',
        width: 200,
        renderCell: renderLocationsCell,
        sortable: false
    },
    {
        field: 'priority',
        headerName: 'Order',
        width: 80
    },
    {
        field: 'description',
        headerName: 'Description',
        width: 300,
        valueFormatter: formatValue('-')
    },
    {
        field: 'terms',
        headerName: 'Terms and Conditions',
        width: 300,
        valueFormatter: formatValue('-')
    },
    {
        field: 'token',
        headerName: 'Discount Code',
        width: 180,
        valueFormatter: formatValue('-')
    },
    {
        field: 'auto',
        headerName: 'Auto Redemption',
        type: 'boolean',
        width: 150
    },
    {
        field: 'multi',
        headerName: 'Multi Redemption',
        type: 'boolean',
        width: 150
    }
];

export const PerksGrid: React.FC<PerksGridProps> = ({
    data,
    isLoading,
    columnsToExclude,
    selectedRow,
    searchValue,
    handleSelectRow,
    storageName
}) => {
    const { locations } = useSelector((state: ApplicationState) => state.locations);

    const rows = React.useMemo(
        () =>
            data
                .map(item => ({
                    ...item,
                    id: item._id,
                    description: item.visualisation?.description,
                    template: titleize(item.template?.replace('_', ' '), true),
                    enabled: 'enabled' in item ? item.enabled !== false : true,
                    multi: 'points' in item && item.points.redemption === 0,
                    locations: 'locationIds' in item && isArray(item.locationIds) ? item.locationIds : [],
                    terms: 'terms' in item ? item.terms : item.visualisation?.terms,
                    totalAwarded:
                        isDefined(item.points) && 'totalAwarded' in item.points
                            ? item.points.totalAwarded
                            : undefined,
                    maxTotalRedemptions:
                        isDefined(item.points) && 'maxTotalRedemptions' in item.points
                            ? item.points.maxTotalRedemptions
                            : undefined,
                    totalRedeemed:
                        isDefined(item.points) && 'totalRedeemed' in item.points
                            ? item.points.totalRedeemed
                            : undefined
                }))
                .filter(item => {
                    if (!searchValue) {
                        return true;
                    }

                    const searchValueLower = searchValue.toLowerCase().trim();
                    const filteredLocationIds = locations
                        .filter(({ title }) =>
                            title.toString().toLocaleLowerCase().includes(searchValueLower)
                        )
                        .map(({ _id }) => _id);
                    return (
                        Object.entries(item)
                            .filter(([field]) => SEARCHABLE_FIELDS.includes(field))
                            .some(([, value]) =>
                                value ? value.toString().toLowerCase().includes(searchValueLower) : false
                            ) ||
                        (filteredLocationIds.length > 0 &&
                            (!item.locations?.length ||
                                item.locations.some(locationId => filteredLocationIds.includes(locationId))))
                    );
                }),
        [data, locations, searchValue]
    );

    const columns = React.useMemo(
        () => gridColumns.filter(column => !columnsToExclude.includes(column.field)),
        [columnsToExclude]
    );

    return (
        <Paper sx={{ height: '100%', display: 'flex' }}>
            <MuiGrid
                rows={rows}
                columns={columns}
                loading={isLoading}
                rowSelectionModel={selectedRow}
                onRowSelectionModelChange={handleSelectRow}
                disableMultipleRowSelection
                hideFooter={false}
                disableColumnFilter
                storageName={storageName}
                disableRowSelectionOnClick={false}
            />
        </Paper>
    );
};
